import React, { useState, useEffect, useRef } from 'react';
import { getAllSupervisors, getEmployeesBySupervisors } from "../../service/employee/employeeService";
import { useDispatch } from 'react-redux';
import { GetPaginationRowsCount } from '../../shared/functions/GetPaginationRowsCount';
import { GetPaginationRowsInterface } from '../../dtos/common/GetPaginationRows';
import { EmployeeDto } from '../../dtos/employee/EmployeeDto';
import { MainPerformanceDto } from '../../dtos/performance/MainPerformanceDataDto';
import { ISTToDate } from '../../shared/functions/ISTToDate';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Button } from 'primereact/button';
import { GetPreviousMonth } from '../../shared/functions/GetPreviousMonth';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'react-datepicker/dist/react-datepicker.css';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';

// Type definitions
interface Employee {
    id: number;
    employeeCode: string;
    emailAddress: string;
    fullName: string;
    name: string;
    isActive: boolean;
    roleNames: string[];
    supervisorId: number | null;
    creationTime: string;
    lastLoginTime?: string | null;
}

interface Review {
    id: number;
    EmployeeCode: string;
    ReviewerID: number;
    IsSubmitted: boolean;
    SubmittedDate: string | null;
    createdAt: string;
    updatedAt: string;
    AdminReason: string | null;
}

interface EmployeeTableData {
    id: number;
    employeeCode: string;
    fullName: string;
    roleNames: string[];
    supervisorId: number | null;
    supervisorName: string;
    reviewStatus: 'completed' | 'pending';
}

export interface LazyState {
    page: number;
    first: number;
    rows: number;
    sortField: string;
    sortOrder: number;
    filters?: string;
}

interface Stats {
    total: number;
    completed: number;
    pending: number;
}

const EmployeeReviewTracker: React.FC = () => {
    const [selectedSupervisor, setSelectedSupervisor] = useState<any[]>([]);
    const [allSupervisors, setAllSupervisors] = useState<any[]>([]);
    const [mockEmployees, setMockEmployees] = useState<Employee[] | []>([])
    const [employeeTableData, setEmployeeTableData] = useState<EmployeeTableData[]>([]);
    const [stats, setStats] = useState<Stats>({
        total: 0,
        completed: 0,
        pending: 0
    });
    const toast = useRef<Toast>(null);
    const [allReviews, setAllReviews] = useState<MainPerformanceDto[] | []>([])
    const [totalSubordinates, setTotalSubordinates] = useState<number>(0);
    const dispatch = useDispatch()
    const row: GetPaginationRowsInterface = GetPaginationRowsCount();
    const [searchText, setSearchText] = useState<string>("");
    const [date, setDate] = useState<any>(GetPreviousMonth(new Date()));
    const [lazyState, setlazyState] = React.useState({
        page: 0,
        first: 0,
        rows: row.rowCount,
        sortField: "createdAt",
        sortOrder: 0 as any,
    });
    const [disableSelectBtn, setDisableSelectBtn] = useState<boolean>(false);

    const handlePrevMonth = () => {
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        firstDay.setMonth(firstDay.getMonth() - 1);
        setDate(firstDay);
    };
    const onPageChange = (event: any) => {
        setlazyState(event);
    };

    const onSort = (event: any) => {
        setlazyState((prev: LazyState) => {
            const newState: LazyState = {
                ...prev, // Copy all properties from the previous state
                sortOrder: event.sortOrder, // Update sortOrder
                sortField: event.sortField, // Update sortField
            };
            return newState;
        });
    };

    const handleNextMonth = () => {
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        firstDay.setMonth(firstDay?.getMonth() + 1);

        if (
            firstDay.getFullYear() > new Date().getFullYear() ||
            (firstDay.getFullYear() === new Date().getFullYear() && firstDay.getMonth() >= new Date().getMonth())
        ) {
            toast.current?.show({ severity: 'warn', summary: 'Sorry!', detail: "You can't go to the current and upcoming months!", life: 3000 });
            return;
        }
        setDate(firstDay);
    };

    //handle date input
    const handleDate = (e: any) => {
        //if the date is upcoming date then set it current date
        const today = new Date();
        if (e.getFullYear() > today.getFullYear() || e.getFullYear() === today.getFullYear() && e.getMonth() >= today.getMonth()) {
            setDate((prev: any) => {
                const newDate = prev;
                return newDate;
            });
            toast?.current?.show({
                severity: "warn",
                summary: "Sorry!",
                detail: "You can't go to the current and upcoming months!",
                life: 3000,
            });
            return; // Prevent further actions if the date is in the future
        }
        setDate(e);
    }

    const fetchSupervisors = async () => {
        const supervisors = await getAllSupervisors(dispatch)
        setAllSupervisors(supervisors)
    }

    useEffect(() => {
        buildEmployeeData();
    }, [date, lazyState])


       useEffect(() => {
        if (selectedSupervisor.length >= 0) { // This will run even when array is empty
            setlazyState((prev) => ({
                ...prev,
                page: 0,
                first: 0
            }));
        }
    }, [selectedSupervisor])


    useEffect(() => {
        fetchSupervisors()
    }, [])

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            buildEmployeeData();
        }, 500); // 500ms debounce delay

        return () => clearTimeout(delayDebounce); // cleanup previous timeout on text change
    }, [searchText]);


    const buildEmployeeData = async () => {
        try {
            const ids = selectedSupervisor?.map((emp) => emp.id)
            // Fetch data first
            const subordinatesResult = await getEmployeesBySupervisors(dispatch, {
                Limit: lazyState.rows,
                Page: lazyState.page,
                Status: "active",
                SortOrder: lazyState.sortOrder,
                SortField: lazyState.sortField,
                SearchValue: searchText?.toLowerCase(),
                submittedDate: ISTToDate(date as unknown as Date),
                supervisorIds: ids
            });
            setTotalSubordinates(subordinatesResult.totalResult)

            // Process employees and add supervisor information
            const tableData: EmployeeTableData[] = subordinatesResult.users.map((emp: EmployeeDto) => {

                return {
                    id: emp.id,
                    employeeCode: emp.employeeCode,
                    fullName: emp.fullName,
                    roleNames: emp.roleNames,
                    supervisorId: emp.supervisorId || null,
                    supervisorName: emp.supervisorName,
                    reviewStatus: emp?.isReviewSubmitted ? "completed" : "pending"
                };
            });

            // Update all states
            setMockEmployees(subordinatesResult.users as Employee[]);
            setEmployeeTableData(tableData);
            setStats({
                total: subordinatesResult.users.length,
                completed: subordinatesResult.totalCompletedReviews,
                pending: subordinatesResult.totalPendingReviews,
            });
        } catch (error) {
            console.error("Error building employee data:", error);
        }
    };


    const CustomInput = React.forwardRef<HTMLButtonElement, { value?: string; onClick?: () => void }>(
        ({ value, onClick }, ref) => (
            <button
                className="btn btn-outline-secondary"
                onClick={onClick}
                ref={ref}
                type="button"
            >
                {value || 'Select month'}
            </button>
        )
    );

    // Column templates for DataTable
    const employeeNameTemplate = (rowData: EmployeeTableData) => {
        return (
            <div className="d-flex align-items-center">
                <div>
                    <div className={`fw-bold d-flex align-items-center ${rowData.reviewStatus === 'completed' ? "text-success" : "text-danger"}`}>
                        {rowData.fullName}
                        <div className="ml-2">
                            {rowData.reviewStatus === 'completed' ? (
                                <i className="pi pi-check-circle text-success" style={{ fontSize: '1.2rem' }}></i>
                            ) : (
                                <i className="pi pi-exclamation-circle text-danger" style={{ fontSize: '1.2rem' }}></i>
                            )}
                        </div>
                    </div>
                    <div className="text-muted small">{rowData.employeeCode}</div>
                </div>
            </div>
        );
    };

    const roleTemplate = (rowData: EmployeeTableData) => {
        return <span className="text-secondary">{rowData.roleNames.join(', ')}</span>;
    };

    const supervisorTemplate = (rowData: EmployeeTableData) => {
        return (
            <span className={rowData.supervisorName === 'No Supervisor' ? 'text-muted font-italic' : 'text-dark'}>
                {rowData.supervisorName}
            </span>
        );
    };

    const statusTemplate = (rowData: EmployeeTableData) => {
        const isCompleted = rowData.reviewStatus === 'completed';
        const label = isCompleted ? 'Review Done' : 'Review Pending';

        return (
            <div className="flex justify-center w-full">
                <Button
                    label={label}
                    className={`text-xs ${isCompleted ? 'bg-green-600' : 'bg-red-600'}`}
                    style={{
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.5rem',
                        cursor: 'default',
                        fontSize: '0.75rem',
                        lineHeight: '1rem'
                    }}
                    disabled
                />
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} />

            <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
                <div className="mb-4">
                    <h3 className="display-7 fw-bold text-dark mb-2">Employee Review Tracker</h3>
                    <p className="text-muted">Track review completion status across your organization</p>
                </div>

                {/* Controls */}

                <Card className="mb-4 p-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                        {/* Left side: Month Picker */}
                        <div className='d-flex gap-3'>
                            <InputText
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Search..."
                                className="w-auto"
                                style={{ minWidth: "150px" }}
                            />
                            <MultiSelect
                                value={selectedSupervisor}
                                onChange={(e) => setSelectedSupervisor(e.value)}
                                options={allSupervisors}
                                optionLabel="fullName"
                                filter
                                resetFilterOnHide
                                placeholder="Select Supervisors"
                                maxSelectedLabels={3}
                                className="multiselect-custom w-auto"
                                style={{ minWidth: "220px" }}
                            />

                        </div>
                        {/* Right side: Supervisor & Search */}
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    icon="pi pi-chevron-left"
                                    onClick={handlePrevMonth}
                                    className="p-button-outlined"
                                    disabled={disableSelectBtn}
                                />
                                <DatePicker
                                    selected={date}
                                    onChange={handleDate}
                                    customInput={<CustomInput />}
                                    dateFormat="MMMM yyyy"
                                    showMonthYearPicker
                                />
                                <Button
                                    icon="pi pi-chevron-right"
                                    onClick={handleNextMonth}
                                    className="p-button-outlined"
                                    disabled={disableSelectBtn}
                                />
                            </div>


                        </div>
                    </div>
                </Card>


                {/* Statistics */}
                <div className="row g-4 mb-4">
                    <div className="col-md-4">
                        <Card className="h-100">
                            <div className="d-flex align-items-center">
                                <div className="bg-primary bg-opacity-10 rounded-3 p-3 me-3">
                                    <i className="pi pi-users text-primary" style={{ fontSize: '2rem' }}></i>
                                </div>
                                <div>
                                    <div className="small text-muted fw-medium">Total Employees</div>
                                    <div className="fs-2 fw-bold text-dark">{totalSubordinates}</div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="col-md-4">
                        <Card className="h-100">
                            <div className="d-flex align-items-center">
                                <div className="bg-success bg-opacity-10 rounded-3 p-3 me-3">
                                    <i className="pi pi-check-circle text-success" style={{ fontSize: '2rem' }}></i>
                                </div>
                                <div>
                                    <div className="small text-muted fw-medium">Reviews Completed</div>
                                    <div className="fs-2 fw-bold text-dark">{stats.completed}</div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="col-md-4">
                        <Card className="h-100">
                            <div className="d-flex align-items-center">
                                <div className="bg-danger bg-opacity-10 rounded-3 p-3 me-3">
                                    <i className="pi pi-exclamation-circle text-danger" style={{ fontSize: '2rem' }}></i>
                                </div>
                                <div>
                                    <div className="small text-muted fw-medium">Reviews Pending</div>
                                    <div className="fs-2 fw-bold text-dark">{stats.pending}</div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Employee DataTable */}
                <Card className="empolyee_tableCont">
                    <h5 className="mb-4">Employee Review Status</h5>
                    {employeeTableData.length > 0 ? (
                        <DataTable
                            className="empolyee_table"
                            value={employeeTableData}
                            // className="p-datatable-sm"
                            stripedRows
                            paginator
                            first={lazyState.first}
                            rows={lazyState.rows}
                            totalRecords={totalSubordinates}
                            onSort={onSort}
                            onPage={onPageChange}
                            sortOrder={lazyState.sortOrder}
                            sortField={lazyState.sortField}
                            showGridlines
                            lazy
                            rowsPerPageOptions={row.rowList}
                            currentPageReportTemplate="Total Records {totalRecords}"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        >
                            <Column
                                field="fullName"
                                header="Employee Details"
                                body={employeeNameTemplate}
                                style={{ width: '30%' }}
                                sortable
                            />
                            <Column
                                field="roleNames"
                                header="Role(s)"
                                body={roleTemplate}
                                style={{ width: '25%' }}
                            />
                            <Column
                                field="supervisorName"
                                header="Supervisor"
                                body={supervisorTemplate}
                                style={{ width: '25%' }}
                            />
                            <Column
                                field="reviewStatus"
                                header="Review Status"
                                body={statusTemplate}
                                style={{ width: '20%' }}
                            />
                        </DataTable>
                    ) : (
                        <div className="text-center py-5">
                            <div className="text-muted mb-3">
                                <i className="pi pi-info-circle" style={{ fontSize: '3rem' }}></i>
                            </div>
                            <p className="text-muted">No employees found for the selected period.</p>
                        </div>
                    )}
                </Card>
            </div>
        </>
    );
};

export default EmployeeReviewTracker;